import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {assert} from 'Asserts';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import FolderSelector from 'file-browser/FolderSelector';
import FolderDefaults, {FolderDefaultsKey} from 'FolderDefaults';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {AllTagSchemasQuery} from 'registry/graphql/__generated/AllTagSchemasQuery';
import {ALL_TAG_SCHEMAS_QUERY} from 'registry/graphql/AllTagSchemasQuery';
import TagSchemaSelector from 'schemas/shared/TagSchemaSelector';
import {filterTagSchemasGraphQL} from 'tag-schema/filterTagSchemas';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import Modal from 'ui-library/modals/Modal';

export interface PrimerPropertiesModalProps {
  onRequestClose: () => void;
  onRequestSubmit: (settings: {folderId: string; tagSchemaId?: string}) => void;
}

interface State {
  folderId: string | null;
  tagSchemaId: string | null;
}

export interface EnhancedProps {
  hasSequenceSchemas: boolean;
}

export class PrimerPropertiesModal extends React.Component<
  PrimerPropertiesModalProps & EnhancedProps,
  State
> {
  state: State = {
    folderId: FolderDefaults.get(FolderDefaultsKey.OLIGO) || null,
    tagSchemaId: null,
  };

  handleChangeFolderId = (e: {target: {value: string | null}}): void => {
    this.setState({folderId: e.target.value || null});
  };

  handleChangeTagSchemaId = (tagSchema: {id: string} | null): void => {
    this.setState({tagSchemaId: tagSchema ? tagSchema.id : null});
  };

  handleRequestSubmit = (): void => {
    assert(Boolean(this.state.folderId), 'Cannot save primers without a folder');
    FolderDefaults.set(FolderDefaultsKey.OLIGO, this.state.folderId!);
    // @ts-expect-error
    this.props.onRequestSubmit(this.state);
  };

  render(): JSX.Element {
    return (
      <Modal
        buttons={[
          {
            text: 'Create',
            submit: true,
            disabled: !this.state.folderId,
            dataTestAttrs: {component: 'PrimerPropertiesModal', element: 'createButton'},
          },
        ]}
        onRequestClose={this.props.onRequestClose}
        onRequestSubmit={this.handleRequestSubmit}
        title="Select Primer Properties"
        dataTestAttrs={{component: 'PrimerPropertiesModal', element: 'root'}}
      >
        <FieldLabel label="Folder" isRequired={true} />
        <FolderSelector
          className={css(styles.folderSelector)}
          onChange={this.handleChangeFolderId}
          showInventory={true}
          value={this.state.folderId}
          unsetOnFetchFail={true}
        />
        {this.props.hasSequenceSchemas && (
          <div>
            <FieldLabel label="Schema" />
            <TagSchemaSelector
              allowNone={true}
              oligoAwareFolderItemTypes={[ModelTypeConstants.DNA_OLIGO]}
              onChange={this.handleChangeTagSchemaId}
              selectedTagSchemaId={this.state.tagSchemaId}
            />
          </div>
        )}
      </Modal>
    );
  }
}

function QueryingPrimerPropertiesModal(props: PrimerPropertiesModalProps): JSX.Element {
  return (
    <BenchlingQuery<AllTagSchemasQuery> query={ALL_TAG_SCHEMAS_QUERY} renderLoading={() => null}>
      {(data) => (
        <PrimerPropertiesModal
          {...props}
          hasSequenceSchemas={
            filterTagSchemasGraphQL(data.tagSchemas, {
              oligoAwareFolderItemTypes: [ModelTypeConstants.DNA_OLIGO],
            }).length > 0
          }
        />
      )}
    </BenchlingQuery>
  );
}

export default QueryingPrimerPropertiesModal;

const styles = StyleSheet.create({
  folderSelector: {
    marginBottom: '10px',
  },
});
